import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DatasourceService {
  private deepConfigEndPoint = '/dashboard/deepconfig/';
  private moduleEndPoint = '/dashboard/module/';
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient) { }

  private extractData(res: Response) {
    const body = res;
    return body || {};
  }

  // Module Methods

  getModuleList(data): Observable<any> {
    const endPoint = this.moduleEndPoint + 'list';
    const dataToSend = {
      'params': data
    };
    return this.http.get<any>(endPoint, { ...this.httpOptions, ...dataToSend }).pipe(
      map(this.extractData)
    );
  }

  getModuleDetails(appId, moduleId): Observable<any> {
    const endPoint = this.moduleEndPoint + appId + '/' + moduleId;
    return this.http.get<any>(endPoint, { ...this.httpOptions }).pipe(
      map(this.extractData)
    );
  }

  getModuleListByFilters(app_id, offset, limit, query?): Observable<any> {
    const params = {
      'app_id': app_id,
      'offset': offset,
      'limit': limit
    };
    if (query) {
      params['q'] = query;
    }
    return this.getModuleList(params);
  }

  createModule(params): Observable<any> {
    return this.http.post<any>(this.moduleEndPoint, params, this.httpOptions).pipe(
      map(this.extractData)
    );
  }

  createModuleByParams(name, desc, deepconfig_ids, app_id): Observable<any> {
    const params = {
      'name': name,
      'deepconfig_ids': deepconfig_ids,
      'desc': desc,
      'app_id': app_id
    };
    return this.createModule(params);
  }

  updateModule(moduleId, name, desc, deepconfig_ids, app_id): Observable<any> {
    const endPoint = this.moduleEndPoint + moduleId;
    const params = {
      'name': name,
      'desc': desc,
      'deepconfig_ids': deepconfig_ids,
      'app_id': app_id
    };
    return this.http.put<any>(endPoint, params, this.httpOptions).pipe(
      map(this.extractData)
    );
  }

  deleteModule(appId, moduleId): Observable<any> {
    const endPoint = this.moduleEndPoint + appId + '/' + moduleId;
    return this.http.delete<any>(endPoint, this.httpOptions).pipe(
      map(this.extractData)
    );
  }

  // Deep config methods

  getOrphanDeepConfigCount(appId, data?) {
    const endPoint = this.deepConfigEndPoint + appId + '/orphandeepconfigcount';
    const dataToSend = {
      'params': data
    };
    return this.http.get<any>(endPoint, { ...this.httpOptions, ...dataToSend }).pipe(
      map(this.extractData)
    );
  }

  createDeepConfig(data): Observable<any> {
    const params = data;
    return this.http.post<any>(this.deepConfigEndPoint, params, this.httpOptions).pipe(
      map(this.extractData)
    );
  }

  createDeepConfigByParams(key, type, app_id, details, desc, is_active, module_id?): Observable<any> {
    const params = {
      'key': key,
      'type': type,
      'app_id': app_id,
      'details': details,
      'desc': desc,
      'is_active': is_active
    };
    if (module_id) {
      params['module_id'] = module_id;
    }
    return this.createDeepConfig(params);
  }

  updateDeepConfig(deepConfigId, key, type, app_id, details, desc, is_active, module_id?): Observable<any> {
    const endPoint = this.deepConfigEndPoint + deepConfigId;
    const params = {
      'key': key,
      'type': type,
      'app_id': app_id,
      'details': details,
      'desc': desc,
      'is_active': is_active
    };
    if (module_id) {
      params['module_id'] = module_id;
    }
    return this.http.put<any>(endPoint, params, this.httpOptions).pipe(
      map(this.extractData)
    );
  }

  attachDeepConfigsToModule(appId, deepConfigList, moduleId) {
    const endPoint = this.moduleEndPoint + moduleId + '/attach';
    const params = {
      'app_id': appId,
      'deepconfig_ids': deepConfigList
    };
    return this.http.put<any>(endPoint, params, this.httpOptions).pipe(
      map(this.extractData)
    );
  }

  detachDeepConfigsFromModule(appId, deepConfigList, moduleId) {
    const endPoint = this.moduleEndPoint + moduleId + '/detach';
    const params = {
      'app_id': appId,
      'deepconfig_ids': deepConfigList
    };
    return this.http.put<any>(endPoint, params, this.httpOptions).pipe(
      map(this.extractData)
    );
  }

  deleteDeepConfig(deepConfigId, appId): Observable<any> {
    // add appId to pass VAPT test
    const endPoint = this.deepConfigEndPoint + appId + '/' + deepConfigId;
    return this.http.delete<any>(endPoint, this.httpOptions).pipe(
      map(this.extractData)
    );
  }

  getDeepConfigList(data): Observable<any> {
    const endPoint = this.deepConfigEndPoint + 'list';
    const dataToSend = {
      'params': data
    };
    return this.http.get<any>(endPoint, { ...this.httpOptions, ...dataToSend }).pipe(
      map(this.extractData)
    );
  }

  getDeepConfigListByFilters(isOrphan, offset, limit, appId, query?, type?, status?, moduleIdList?): Observable<any> {
    const params = {
      'app_id': appId,
      'offset': offset,
      'limit': limit,
      'orphan': isOrphan
    };
    if (query) {
      params['q'] = query;
    }

    if (type) {
      params['type'] = type;
    }

    if (status) {
      params['status'] = status;
    }

    if (moduleIdList) {
      params['module_ids'] = moduleIdList;
    }
    return this.getDeepConfigList(params);
  }

  getDeepConfigListById(appId, idList): Observable<any> {
    const params = {
      'app_ids': [appId],
      'ids': idList
    };
    return this.getDeepConfigList(params);
  }

  getDeepConfigListByModule(appId, moduleId): Observable<any> {
    const params = {
      'app_ids': [appId],
      'module_id': moduleId
    };
    return this.getDeepConfigList(params);
  }

  getDeepConfigDetails(appId, deepConfigId): Observable<any> {
    const endPoint = this.deepConfigEndPoint + appId + '/' + deepConfigId;
    return this.http.get<any>(endPoint, this.httpOptions).pipe(
      map(this.extractData)
    );
  }

  uploadLocaleFile() { }

  uploadImage() { }

  downloadLocaleFile(file_id): Observable<any> {
    const endPoint = this.deepConfigEndPoint + 'downloadlocale/' + file_id;
    return this.http.get<any>(endPoint, this.httpOptions).pipe(
      map(this.extractData)
    );
  }
  copyLocaleFile(file_id, app_id): Observable<any> {
    const endPoint = this.deepConfigEndPoint + 'copylocale';
    const params = {
      'file_id': file_id,
      'app_id': app_id
    };
    return this.http.post<any>(endPoint, params, this.httpOptions).pipe(
      map(this.extractData)
    );
  }
  bulkUpdateDeepConfig(updatedDeepConfigs, moduleId): Observable<any> {
    const endPoint = this.deepConfigEndPoint + 'update/';
    updatedDeepConfigs.module_id = moduleId;
    return this.http.post<any>(endPoint, updatedDeepConfigs, this.httpOptions).pipe(
      map(this.extractData)
    );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure

      // TODO: better job of transforming error for user consumption

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  getLocaleList(appId: any) {
    const url = 'dashboard/locale/list?app_id=' + appId;
    return this.http.get<any>(url).pipe(
      map((res: Response) => {
        const body = res;
        return body || {};
      })
    );
  }

}
